/* ENDING A COACHING RELATIONSHIP — one function, shared by the roster's bulk action and by
 * whatever single-row action the client page grows.
 *
 * 🔴 THEIR `Archive client` IS OUR `ended`, and it is the only bulk action worth copying. §16
 * measured their whole bulk menu and it holds exactly `Send invite` and `Archive client` — no
 * bulk training assignment exists on his plan at all. We do not copy `Send invite`, because we
 * SEND NOTHING (F13): a button whose only effect is to look like it worked is the dead-button
 * shape this roster has already fixed twice tonight.
 *
 * ⚠️ WHAT ENDING COSTS IS NAMED WHERE IT IS DONE, not buried in a doc. `DECISIONS.md` D-V8.2 is
 * the decision: when a relationship ends, BOTH SIDES KEEP READING the thread and NOBODY MAY SEND.
 * The athlete keeps every session they logged — nothing is deleted — and they need a fresh
 * invitation to be coached again. A confirm that says only "Are you sure?" is a confirm that
 * teaches people to click through confirms.
 *
 * ⚠️ TWO COLUMNS, AND EXACTLY TWO. The coach's grant on `coach_clients` is column-level:
 * `UPDATE(status, ended_at, invited_*)`. Writing anything else is refused by Postgres rather than
 * by this file — but a payload that names a third column would fail the WHOLE update, so the
 * payload is built in one place and asserted by a test instead of being typed at each call site.
 */

export type EndCoachingPayload = { status: "ended"; ended_at: string };

/** The exact row update. Pure, so a test can read it without a database. */
export function endCoachingPayload(now: Date): EndCoachingPayload {
  // ISO with a Z, which is what every other timestamp this app writes uses — a local-time string
  // here would be an hour wrong twice a year and nothing would say so.
  return { status: "ended", ended_at: now.toISOString() };
}

/**
 * The narrowest client this needs, so a test can pass a recorder instead of Supabase.
 *
 * ⚠️ `PromiseLike`, NOT `Promise` — and the difference is not pedantry. A Supabase query builder
 * is a THENABLE: it has `.then` and executes when awaited, but it is not a `Promise` and has no
 * `catch`/`finally`. Typing this as `Promise` made the real client fail to satisfy the very
 * interface it was written for, while the test's hand-made recorder passed happily. A fixture
 * that fits a shape production cannot produce is this repo's own trap, in a type system.
 */
export type UpdatableClient = {
  from: (table: string) => {
    update: (values: EndCoachingPayload) => {
      in: (column: string, values: string[]) => PromiseLike<{ error: { message: string } | null }>;
    };
  };
};

/**
 * End coaching for one or many link ids.
 *
 * ⚠️ IT TAKES LINK IDS, NEVER CLIENT IDS. `coach_clients.id` is the relationship; `client_id` is
 * the person, and a person can have had more than one relationship row over time. Ending by the
 * wrong key would reach into a row this coach may not own — which RLS would refuse, so the
 * symptom would be a silent no-op rather than an error anybody notices.
 */
export async function endCoaching(
  client: UpdatableClient,
  linkIds: string[],
  now: Date = new Date(),
): Promise<{ error: string | null; ended: number }> {
  // AN EMPTY SELECTION IS NOT AN UPDATE WITH NO ROWS — `in("id", [])` is a request that can
  // legitimately match everything in some query builders. It never reaches the wire.
  if (linkIds.length === 0) return { error: null, ended: 0 };
  const { error } = await client
    .from("coach_clients")
    .update(endCoachingPayload(now))
    .in("id", linkIds);
  return { error: error === null ? null : error.message, ended: error === null ? linkIds.length : 0 };
}

/**
 * N2g (2026-10-06) — WITHDRAWING A PENDING LINK is the same write as ending (`pending → ended`, the existing UPDATE(status)
 * grant, rehearsed W83-3); the row then reads Archived. The phone's own words (`ClientsView`'s confirm), one sentence.
 */
export const WITHDRAW_INVITATION_WARNING = "Withdraw this invitation? The link stops working. The row moves to Archived.";

/**
 * The sentence the confirm shows. Exported so a test can assert it names the COST rather than
 * asking "are you sure".
 */
export function endCoachingWarning(count: number): string {
  const who = count === 1 ? "this client" : `these ${count} clients`;
  return (
    `End coaching for ${who}? They keep their history and you both keep reading the thread, ` +
    `but nobody can send, and they will need a new invitation to be coached again.`
  );
}
